import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { LIVE_LINK } from '../helpers/url_helper'

export const marketingApi = createApi({
  reducerPath: 'marketingApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${LIVE_LINK}/marketing` }),
  tagTypes: ['subs', 'domains'],
  endpoints: (build) => ({
    // Subscriptions page
    fetchAllSubs: build.query({
      query: () => ({
        url: '/subs'
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.subs.map(({ _id }) => ({ type: 'subs', id: _id })), // Provide individual tags for each item
              { type: 'subs', id: 'LIST' } // Special tag for the whole list
            ]
          : [{ type: 'subs', id: 'LIST' }],
      refetchOnMountOrArgChange: true, // Add this line
    }),
    addSub: build.mutation({
      query: (newRow) => ({
        url: '/subs',
        method: 'POST',
        body: newRow
      }),
      async onQueryStarted(newRow, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const patchResult = dispatch(
          marketingApi.util.updateQueryData('fetchAllSubs', undefined, (draft) => {
            draft.subs.push(data); // Use actual returned data
          })
        );
        try {
          await queryFulfilled
        } catch {
          patchResult.undo(); // Revert optimistic addition on failure
        }
      },
      invalidatesTags: [{ type: 'subs', id: 'LIST' }]
    }),
    updateSub: build.mutation({
      query: (updatedRow) => ({
        url: `/subs/${updatedRow._id}`,
        method: 'PUT',
        body: updatedRow
      }),
      async onQueryStarted(updatedRow, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          marketingApi.util.updateQueryData('fetchAllSubs', undefined, (draft) => {
            const item = draft.subs.find((row) => row._id === updatedRow._id);
            if (item) {
              Object.assign(item, updatedRow); // Apply changes optimistically
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo(); // Revert optimistic update on failure
        }
      },
      invalidatesTags: (result, error, updatedRow) => [
        { type: 'subs', id: updatedRow._id },
        { type: 'subs', id: 'LIST' }
      ]
    }),
    deleteSub: build.mutation({
      query: (id) => ({
        url: `/subs/${id}`,
        method: 'DELETE'
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          marketingApi.util.updateQueryData('fetchAllSubs', undefined, (draft) => {
            const index = draft.subs.findIndex((row) => row._id === id);
            if (index !== -1) {
              draft.subs.splice(index, 1); // Remove item optimistically
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo(); // Revert optimistic deletion on failure
        }
      },
      invalidatesTags: (result, error, id) => [
        { type: 'subs', id },
        { type: 'subs', id: 'LIST' }
      ]
    }),
    // Domains page
    fetchAllDomains: build.query({
      query: () => ({
        url: '/domains'
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.domains.map(({ _id }) => ({ type: 'domains', id: _id })), // Provide individual tags for each item
              { type: 'domains', id: 'LIST' } // Special tag for the whole list
            ]
          : [{ type: 'domains', id: 'LIST' }],
      refetchOnMountOrArgChange: true, // Add this line
    }),
    getCredentials: build.mutation({
      query: (credentials) => ({
        url: `/domains/credentials`,
        method: 'PUT',
        body: credentials
      })
    }),
    addDomain: build.mutation({
      query: (newRow) => ({
        url: '/domains',
        method: 'POST',
        body: newRow
      }),
      async onQueryStarted(newRow, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const patchResult = dispatch(
          marketingApi.util.updateQueryData('fetchAllDomains', undefined, (draft) => {
            draft.domains.push(data); // Use actual returned data
          })
        );
        try {
          await queryFulfilled
        } catch {
          patchResult.undo(); // Revert optimistic addition on failure
        }
      },
      invalidatesTags: [{ type: 'domains', id: 'LIST' }]
    }),
    updateDomain: build.mutation({
      query: (updatedRow) => ({
        url: `/domains/${updatedRow._id}`,
        method: 'PUT',
        body: updatedRow
      }),
      async onQueryStarted(updatedRow, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          marketingApi.util.updateQueryData('fetchAllDomains', undefined, (draft) => {
            const item = draft.domains.find((row) => row._id === updatedRow._id);
            if (item) {
              Object.assign(item, updatedRow); // Apply changes optimistically
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo(); // Revert optimistic update on failure
        }
      },
      invalidatesTags: (result, error, updatedRow) => [
        { type: 'domains', id: updatedRow._id },
        { type: 'domains', id: 'LIST' }
      ]
    }),
    deleteDomain: build.mutation({
      query: (id) => ({
        url: `/domains/${id}`,
        method: 'DELETE'
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          marketingApi.util.updateQueryData('fetchAllDomains', undefined, (draft) => {
            const index = draft.domains.findIndex((row) => row._id === id);
            if (index !== -1) {
              draft.domains.splice(index, 1); // Remove item optimistically
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo(); // Revert optimistic deletion on failure
        }
      },
      invalidatesTags: (result, error, id) => [
        { type: 'domains', id },
        { type: 'domains', id: 'LIST' }
      ]
    }),
    // Subscriptions page

    updateResponsible: build.mutation({
      query: (updatedRow) => ({
        url: `/notifications/${updatedRow._id}`,
        method: 'PUT',
        body: updatedRow
      }),
      async onQueryStarted(updatedRow, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          marketingApi.util.updateQueryData('fetchAllNotifications', undefined, (draft) => {
            const item = draft.notifications.find((row) => row._id === updatedRow._id);
            if (item) {
              Object.assign(item, updatedRow); // Apply changes optimistically
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo(); // Revert optimistic update on failure
        }
      },
      invalidatesTags: (result, error, updatedRow) => [
        { type: 'notifications', id: updatedRow._id },
        { type: 'notifications', id: 'LIST' }
      ]
    })
  })
});